import { z } from 'zod';
import { OUTBOUND_CALL_OUTCOME, type OutboundCallOutcome } from './outbound-call-outcome';
import { requestedCallbackSchema } from '../schemas/outreach-campaign';
import { e164Schema } from '../schemas/phone';

/**
 * Outreach vocabulary shared by the pure decision logic and the database
 * schema. One definition, so `decideNextAction` and the tables can never
 * disagree about what a run's status means.
 *
 * Distinct from {@link OUTBOUND_CALL_OUTCOME}, which is the *call* disposition
 * and is consumed here unchanged — outreach adds no dispositions.
 */

/** Where a run is in its lifecycle. */
export const OUTREACH_RUN_STATUS = {
  /** Due to be advanced by the driver. */
  ACTIVE: 'active',
  /** Claimed by a tick; a dispatch is in flight. */
  DISPATCHING: 'dispatching',
  /** Waiting on a patient-requested callback time. No retries run meanwhile. */
  SUSPENDED: 'suspended',
  /** Another campaign owns this run's subject and is running first. */
  HELD: 'held',
  /** Ended having met the objective. */
  COMPLETED: 'completed',
  /** Ended without meeting the objective. */
  STOPPED: 'stopped',
  /** Ended because something broke — a bad spec, an unresolvable timezone. */
  FAILED: 'failed',
} as const;
export type OutreachRunStatus = (typeof OUTREACH_RUN_STATUS)[keyof typeof OUTREACH_RUN_STATUS];

/** Statuses that occupy the one-live-run-per-campaign slot. */
export const LIVE_RUN_STATUSES: readonly OutreachRunStatus[] = [
  OUTREACH_RUN_STATUS.ACTIVE,
  OUTREACH_RUN_STATUS.DISPATCHING,
  OUTREACH_RUN_STATUS.SUSPENDED,
  OUTREACH_RUN_STATUS.HELD,
];

/** Why a run ended. Every terminal state names one. */
export const OUTREACH_TERMINAL_REASON = {
  /** Objective met during one of our own outbound attempts. */
  MET_ON_CALL: 'met_on_call',
  /** Objective met on an attributed inbound return call. */
  MET_ON_RETURN_CALL: 'met_on_return_call',
  /** Objective met with no attributable outreach contact — walk-in, portal, staff. */
  MET_ELSEWHERE: 'met_elsewhere',

  /** The patient no longer matches the campaign's condition. Not a failure. */
  NO_LONGER_QUALIFIES: 'no_longer_qualifies',
  /** Ran out of steps or attempts without meeting the objective. */
  EXHAUSTED: 'exhausted',
  /**
   * An outcome in the campaign's stop list ended the run, and no more specific
   * reason applies. Distinct from `exhausted`, which means we genuinely used up
   * the sequence — mislabelling one as the other makes a stopped run look like
   * a worked one.
   *
   * `scheduled` lands here when the agent reported a booking the system of
   * record does not have. Success reads the row, not the claim, so the run
   * stops without being marked met — and that gap is worth seeing.
   */
  STOPPED_ON_OUTCOME: 'stopped_on_outcome',
  /** Passed `expiresAfterDays`. */
  EXPIRED: 'expired',
  /**
   * Expired while held behind a conflicting campaign — it never got a turn.
   * Its own reason so starvation is observable rather than silent.
   */
  EXPIRED_WHILE_HELD: 'expired_while_held',

  DECLINED: 'declined',
  WRONG_NUMBER: 'wrong_number',
  /** Spoke with the patient, nothing booked. One real conversation ends outreach. */
  ENGAGED_NO_BOOKING: 'engaged_no_booking',
  ESCALATED: 'escalated',
  OPTED_OUT: 'opted_out',
  DNC: 'dnc',
  NO_PHONE: 'no_phone',
  /**
   * No date of birth on the chart. An outbound call verifies whoever answers
   * against it and nothing else, so it is never placed (ADR-333).
   */
  NO_DOB: 'no_dob',
  CONSENT: 'consent',
  EXCLUDED: 'excluded',
  STOPPED_BY_USER: 'stopped_by_user',
  /** A bad spec, an unresolvable timezone — something an operator must look at. */
  ERROR: 'error',
} as const;
export type OutreachTerminalReason =
  (typeof OUTREACH_TERMINAL_REASON)[keyof typeof OUTREACH_TERMINAL_REASON];

/** Terminal reasons that mean the objective was met. */
export const MET_TERMINAL_REASONS: readonly OutreachTerminalReason[] = [
  OUTREACH_TERMINAL_REASON.MET_ON_CALL,
  OUTREACH_TERMINAL_REASON.MET_ON_RETURN_CALL,
  OUTREACH_TERMINAL_REASON.MET_ELSEWHERE,
];

/**
 * Why an attempt was not placed. Surfaced verbatim in the ops view — "nothing
 * is dialing" must never be explained by silence, so these are user-facing
 * copy, not debug strings.
 */
export const OUTREACH_SKIP_REASON = {
  OUTREACH_DISABLED: 'outreach_disabled',
  CAMPAIGN_INACTIVE: 'campaign_inactive',
  WINDOW_CLOSED: 'window_closed',
  /** Another outreach call to this patient is already in flight. */
  HELD_BY_CAMPAIGN: 'held_by_campaign',
  /** A higher-priority campaign wants this patient in the same tick. */
  HELD_BY_HIGHER_PRIORITY: 'held_by_higher_priority',
  /** Too soon after the last contact, across all campaigns. */
  COOLDOWN: 'cooldown',
  /** The contacts-per-period ceiling for this patient. */
  CONTACT_CEILING: 'contact_ceiling',
  /** Still inside the grace period after leaving a voicemail. */
  VOICEMAIL_GRACE: 'voicemail_grace',
  AT_CAPACITY: 'at_capacity',
  DAILY_CAP: 'daily_cap',
  /** Lost the dial claim — something else was already calling this interaction. */
  ALREADY_DIALING: 'already_dialing',
  /**
   * The referral lost its provider pool or its open slots after the run started
   * (ADR-318, P6). The run holds — deferred an hour at a time, using no attempt —
   * and resumes by itself when slots come back.
   */
  WAITING_FOR_SLOTS: 'waiting_for_slots',
  /**
   * The workflow version this step pinned could not be loaded, or loaded and
   * failed validation (ADR-318, P12). Nothing was dialled, the run is deferred
   * an hour, and VagusX ops were emailed.
   */
  WORKFLOW_UNAVAILABLE: 'workflow_unavailable',
  /**
   * The practice has no valid outbound (or inbound) number to call from
   * (ADR-318, C). Nothing was dialled, the run is deferred an hour, and an ops
   * alert was logged.
   */
  NO_OUTBOUND_NUMBER: 'no_outbound_number',
} as const;
export type OutreachSkipReason = (typeof OUTREACH_SKIP_REASON)[keyof typeof OUTREACH_SKIP_REASON];

/** Lifecycle of one attempt. */
export const OUTREACH_ATTEMPT_STATUS = {
  SCHEDULED: 'scheduled',
  DISPATCHED: 'dispatched',
  RESOLVED: 'resolved',
  SKIPPED: 'skipped',
  FAILED: 'failed',
} as const;
export type OutreachAttemptStatus =
  (typeof OUTREACH_ATTEMPT_STATUS)[keyof typeof OUTREACH_ATTEMPT_STATUS];

/**
 * Did outreach cause the outcome, and how. **Independent of success** — a run
 * can be attributed without succeeding (they rang back and didn't book: a
 * conversion problem) and can succeed without being attributed (they booked on
 * the portal). Collapsing the two would make those indistinguishable.
 */
export const OUTREACH_ATTRIBUTION = {
  /** Happened on a call we placed. */
  DIRECT: 'direct',
  /** Happened on an inbound call the patient placed after we contacted them. */
  RETURN_CALL: 'return_call',
  /** No attributable outreach contact. */
  NONE: 'none',
} as const;
export type OutreachAttribution = (typeof OUTREACH_ATTRIBUTION)[keyof typeof OUTREACH_ATTRIBUTION];

/** What most likely prompted a return call. Both happen; they are different signals. */
export const RETURN_CALL_PROMPT = {
  VOICEMAIL: 'voicemail',
  MISSED_CALL: 'missed_call',
} as const;
export type ReturnCallPrompt = (typeof RETURN_CALL_PROMPT)[keyof typeof RETURN_CALL_PROMPT];

/** Scope of a patient's opt-out. Never conflated — three different requests. */
export const OPTOUT_SCOPE = {
  ALL: 'all',
  VOICE: 'voice',
  SMS: 'sms',
} as const;
export type OptoutScope = (typeof OPTOUT_SCOPE)[keyof typeof OPTOUT_SCOPE];

/** Where an opt-out came from. Part of the evidence trail. */
export const OPTOUT_SOURCE = {
  VOICE_CALL: 'voice_call',
  SMS_KEYWORD: 'sms_keyword',
  STAFF: 'staff',
  PORTAL: 'portal',
} as const;
export type OptoutSource = (typeof OPTOUT_SOURCE)[keyof typeof OPTOUT_SOURCE];

/** Events an organization can choose to have reach a human immediately. */
export const OUTREACH_NOTIFICATION_EVENT = {
  ESCALATED: 'escalated',
  OPTED_OUT: 'opted_out',
  WRONG_NUMBER: 'wrong_number',
  SEQUENCE_EXHAUSTED: 'sequence_exhausted',
  /**
   * A spec that will not parse, a condition query that failed, or a
   * fail-closed opt-out write. **Cannot be switched off** — suppressing a
   * patient because a write failed is otherwise indistinguishable from a
   * genuine opt-out.
   */
  CAMPAIGN_ERROR: 'campaign_error',
} as const;
export type OutreachNotificationEvent =
  (typeof OUTREACH_NOTIFICATION_EVENT)[keyof typeof OUTREACH_NOTIFICATION_EVENT];

/** The one event an organization may not disable. */
export const MANDATORY_NOTIFICATION_EVENTS: readonly OutreachNotificationEvent[] = [
  OUTREACH_NOTIFICATION_EVENT.CAMPAIGN_ERROR,
];

/**
 * The earliest and latest a patient may be called, clinic-local `HH:mm`.
 *
 * **Not configurable, and it lives in code rather than a settings row**: a value
 * in a table can be changed by the same mechanisms that create the risk it
 * guards against. An organization may NARROW it; nothing widens it — not a
 * campaign, not a step, and not a patient's own explicit request, because they
 * cannot consent on a phone call to something that outlives the call and staff
 * are not there when it lands.
 *
 * HERE RATHER THAN IN `apps/api` BECAUSE THERE ARE NOW TWO ENFORCERS. The
 * window resolver clamps a scheduled attempt to it, and the voice agent's
 * `Callback` component refuses a time outside it while the patient is still on
 * the line — which is the only moment the bound can be renegotiated rather than
 * silently applied. A safety constant with two copies is one rename away from
 * having two values.
 */
export const QUIET_HOURS_FLOOR = { start: '08:00', end: '21:00' } as const;

// ───────────────────── Campaign auto-dial (ADR-318) ─────────────────────

/**
 * Why a referral that qualifies for a campaign cannot be called yet (P5).
 *
 * Evaluated in this order, and the FIRST failure is the reason: a referral with
 * no phone and no slots is reported as `no_phone`, because adding slots would
 * not make it callable. A not-ready referral has no `outreach_run` row; its
 * reason is computed when the queue is read.
 */
export const NOT_READY_REASON = {
  /** The patient has no first or last name on file. */
  NO_NAME: 'no_name',
  /** No number we can dial — none on file, or none that parses as dialable. */
  NO_PHONE: 'no_phone',
  /** No date of birth on the chart, and the campaign requires one. */
  NO_DOB: 'no_dob',
  /** No provider the referral could be booked with. */
  NO_POOL: 'no_pool',
  /** That pool has no open slots inside the campaign's horizon. */
  NO_SLOTS: 'no_slots',
  /**
   * Ready, but no clinic location to take a timezone from, so reconcile will
   * not start a run (it counts them as `skippedNoLocation`). Not a readiness
   * check: only the queue reports it (ADR-341), so a patient reconcile can
   * never start is not shown as about to be called.
   */
  NO_LOCATION: 'no_location',
} as const;
export type NotReadyReason = (typeof NOT_READY_REASON)[keyof typeof NOT_READY_REASON];
/**
 * The workflow purposes a referral campaign may run (R11): one made for
 * scheduling a referral, or one only campaigns use (no purpose). One rule for
 * the editor's picker and the server's publish and launch checks.
 */
export const REFERRAL_WORKFLOW_INTENTS: ReadonlyArray<string | null> = ['schedule_referral', null];

/** Can a workflow with this purpose run for this audience? */
export function workflowFitsAudience(audienceType: string, intent: string | null): boolean {
  return audienceType !== 'open_referral' || REFERRAL_WORKFLOW_INTENTS.includes(intent);
}

/** What staff read for each reason: the editor's "Right now" panel and the queue. */
export const NOT_READY_REASON_LABELS: Record<NotReadyReason, string> = {
  no_name: 'No patient name',
  no_phone: 'No phone we can dial',
  no_dob: 'No date of birth',
  no_pool: 'No provider matches',
  no_slots: 'No open slots for their providers',
  no_location: 'No clinic location to call from',
};
export const NOT_READY_REASON_VALUES = Object.values(NOT_READY_REASON) as [
  NotReadyReason,
  ...NotReadyReason[],
];

/**
 * The lanes of a campaign's queue. `ready` and `not_ready` are patients: those
 * who qualify and have no live run, computed when the queue is read. Every
 * other lane is runs, ended ones included, so a patient whose old run is in
 * Done can be in Ready again once they may re-enter. A qualifying patient who
 * is opted out, cooling down, or taken out with no run is in no lane.
 */
export const QUEUE_LANE = {
  /**
   * Qualifies and could be called, but no run has started: the campaign is a
   * draft or paused, or the next membership check has not reached them yet.
   */
  READY: 'ready',
  NOT_READY: 'not_ready',
  /** A live run with a next attempt scheduled. */
  WAITING: 'waiting',
  /** An attempt is dispatched or on the phone right now. */
  CALLING: 'calling',
  /** Suspended until a time the patient asked for. */
  CALLBACK: 'callback',
  /** Held: waiting for slots, or behind another campaign. */
  HOLD: 'hold',
  /** Ended in a way a person has to pick up: wrong number, escalated, exhausted, no date of birth. */
  NEEDS_PERSON: 'needs_person',
  /** Ended any other way. */
  DONE: 'done',
} as const;
export type QueueLane = (typeof QUEUE_LANE)[keyof typeof QUEUE_LANE];

/**
 * The run endings a person has to pick up (P9): the `needs_person` lane, and
 * the note left on the referral's task.
 */
/**
 * What the campaign decided when an attempt's call came back, recorded on the
 * attempt (`outreach_attempt.next_action`). Call follow-ups read it to tell
 * work the campaign will do itself (a callback it is now waiting to place)
 * from work it handed to nobody (plan: docs/plans/call-follow-ups.md §3.2).
 */
export const OUTREACH_ATTEMPT_NEXT_ACTION = {
  /** The objective was met: the run ended a success. */
  MET: 'met',
  /** The run stopped on this call's outcome. */
  STOPPED: 'stopped',
  /** The run is suspended until the time the patient asked to be called. */
  AWAIT_CALLBACK: 'await_callback',
  /** The run carries on with another try. */
  NEXT_TRY: 'next_try',
  /** The run had already ended (opted out, removed) when the result came in. */
  RUN_ENDED: 'run_ended',
  /** The call never reported and its try was handed back (ADR-336, decision 7). */
  GIVEN_BACK: 'given_back',
  /** The run ended because something broke. */
  ERROR: 'error',
} as const;
export type OutreachAttemptNextAction =
  (typeof OUTREACH_ATTEMPT_NEXT_ACTION)[keyof typeof OUTREACH_ATTEMPT_NEXT_ACTION];

export const NEEDS_PERSON_REASONS: readonly OutreachTerminalReason[] = [
  OUTREACH_TERMINAL_REASON.WRONG_NUMBER,
  OUTREACH_TERMINAL_REASON.ESCALATED,
  OUTREACH_TERMINAL_REASON.EXHAUSTED,
  // Somebody has to add the date of birth before this patient can be called.
  OUTREACH_TERMINAL_REASON.NO_DOB,
];
export const QUEUE_LANE_VALUES = Object.values(QUEUE_LANE) as [QueueLane, ...QueueLane[]];

/**
 * The call dispositions that mean "we did not get through".
 *
 * `failed` is included because it is OUR failure, not the patient's answer: a
 * dial that never reached the agent is resolved `failed`, and ending a
 * patient's sequence over a transient telephony fault would be wrong.
 */
export const NOT_REACHED_OUTCOMES: readonly OutboundCallOutcome[] = [
  OUTBOUND_CALL_OUTCOME.NO_ANSWER,
  OUTBOUND_CALL_OUTCOME.VOICEMAIL,
  OUTBOUND_CALL_OUTCOME.UNAVAILABLE,
  OUTBOUND_CALL_OUTCOME.FAILED,
];

/**
 * Every step's `continueOn`, written by the editor and rewritten by publish,
 * so nobody authors it by hand (R1). With it, only `afterLastTry` and the
 * campaign's `stopOn` end a sequence. An empty `continueOn` is the trap this
 * closes: a step with three tries and an empty list stops after its FIRST try
 * (`decideNextAction` step 9).
 *
 * `callback_requested` IS IN IT, though it is not a not-reached outcome. A
 * callback `decideNextAction` honours never gets this far (step 7 suspends the
 * run first). One it does NOT honour — no time it could place, the honour cap
 * reached, or a step set to `use_standard_wait` — falls through to step 9, and
 * without this entry that stopped the run: "use the standard wait" meant "stop
 * calling". With it, the step's next try runs on its standard spacing, which is
 * what the setting says.
 */
export const CONTINUE_ON_OUTCOMES: readonly OutboundCallOutcome[] = [
  ...NOT_REACHED_OUTCOMES,
  OUTBOUND_CALL_OUTCOME.CALLBACK_REQUESTED,
];

/**
 * Typed signals a call reports alongside its disposition (R8).
 *
 * Today these travel as `structuredOutput` entries whose `type` is a free string
 * matched by literal in the completion hook. These names are the contract: the
 * worker emits them and the hook switches on them, and a rename is a compile
 * error on both sides instead of a callback nobody places.
 */
export const OUTREACH_SIGNAL = {
  /** The patient asked to be called at a time. `data` is a `RequestedCallback`. */
  CALLBACK_REQUESTED: 'callback_requested',
  /** A voicemail message was actually delivered (not just a machine reached). */
  VOICEMAIL_LEFT: 'voicemail_left',
  /** The patient asked not to be contacted. */
  OPTOUT_REQUESTED: 'optout_requested',
} as const;
export type OutreachSignalType = (typeof OUTREACH_SIGNAL)[keyof typeof OUTREACH_SIGNAL];

/** `optout_requested.data`: which channel they asked us to stop using. */
export const optoutRequestedDataSchema = z.object({
  scope: z.enum([OPTOUT_SCOPE.VOICE, OPTOUT_SCOPE.SMS, OPTOUT_SCOPE.ALL]),
  /**
   * The number the call was on, E.164, as the voice worker knows it from the
   * call itself (the number dialled, or the caller's line inbound) — never one
   * the model supplied (ADR-318, D2). Optional: absent when the call carried
   * no usable number, and on a signal sent before this existed.
   */
  phone: e164Schema.optional(),
  /**
   * Whether the person who asked had been verified as the patient. False (or
   * absent) means they had not been — someone else on the line, or the patient
   * before identity was settled — so the request is about the NUMBER, not
   * provably the patient's own (ADR-318, D2).
   */
  identityVerified: z.boolean().optional(),
});

/**
 * One signal, `{type, data}` — the same shape as a `structuredOutput` entry, so
 * a signal can be sent in either place during the cut-over.
 */
export const outreachSignalSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(OUTREACH_SIGNAL.CALLBACK_REQUESTED),
    data: requestedCallbackSchema,
  }),
  z.object({ type: z.literal(OUTREACH_SIGNAL.VOICEMAIL_LEFT), data: z.object({}) }),
  z.object({ type: z.literal(OUTREACH_SIGNAL.OPTOUT_REQUESTED), data: optoutRequestedDataSchema }),
]);
export type OutreachSignal = z.infer<typeof outreachSignalSchema>;
